import { formatDistanceToNow as dateFnsFormatDistanceToNow } from "date-fns";

export const ACTIVE_AUDIT_STATUSES = [
	"pending",
	"crawling",
	"analyzing",
	"running",
];

export function isAuditInProgress(status) {
	return ACTIVE_AUDIT_STATUSES.includes(String(status || "").toLowerCase());
}

export function formatDistanceToNow(dateString) {
	if (!dateString) return "";
	try {
		return dateFnsFormatDistanceToNow(new Date(dateString), { addSuffix: true });
	} catch {
		return "";
	}
}

export const AEO_ACTION_CATEGORIES = [
	"faq_readiness",
	"schema",
	"answerability",
	"readability",
	"technical",
	"internal_linking",
	"content_depth",
	"multimodal",
	"crawlability",
	"metadata",
	"brand_recognition",
	"sentiment",
];

export const GEO_ACTION_CATEGORIES = [
	"brand_entity",
	"trust_signals",
	"ai_citation",
	"freshness",
	"brand_recognition",
	"sentiment",
	"ai_discoverability",
	"llms_txt",
	"experience",
	"expertise",
];

export const CONTENT_WRITING_CATEGORIES = [
	"content_depth",
	"answerability",
	"readability",
	"faq_readiness",
	"metadata",
	"brand_entity",
];

export function isContentWritingAction(action) {
	if (!action) return false;
	const category = action.category_tag || action.category;
	return CONTENT_WRITING_CATEGORIES.includes(category);
}

export function filterAeoActions(allActions) {
	if (!Array.isArray(allActions)) return [];
	return allActions.filter(
		(a) =>
			AEO_ACTION_CATEGORIES.includes(a.category) ||
			a.category === "brand_recognition" ||
			a.category === "sentiment"
	);
}

export function filterGeoActions(allActions) {
	if (!Array.isArray(allActions)) return [];
	return allActions.filter((a) => {
		const category = a.category_tag || a.category;
		return GEO_ACTION_CATEGORIES.includes(category);
	});
}

export function buildActionMatrixData(actions) {
	const list = actions || [];
	return {
		quick_wins: list.filter((a) => a.quadrant === "quick_wins"),
		major_projects: list.filter((a) => a.quadrant === "major_projects"),
		fill_ins: list.filter((a) => a.quadrant === "fill_ins"),
		time_wasters: list.filter((a) => a.quadrant === "time_wasters"),
		all_actions: list,
		statistics: {
			total_actions: list.length,
			quick_wins_count: list.filter((a) => a.quadrant === "quick_wins").length,
			major_projects_count: list.filter((a) => a.quadrant === "major_projects").length,
			fill_ins_count: list.filter((a) => a.quadrant === "fill_ins").length,
			time_wasters_count: list.filter((a) => a.quadrant === "time_wasters").length,
			avg_priority_score:
				list.length > 0
					? list.reduce((sum, a) => sum + (a.priority_score || 0), 0) / list.length
					: 0,
		},
	};
}

export function hasSplitScores(summary) {
	if (!summary) return false;
	const aeoScore = summary.aeo_score || 0;
	const geoScore = summary.geo_score || 0;
	const aeoGrade = summary.aeo_grade || "";
	const geoGrade = summary.geo_grade || "";
	return aeoScore > 0 && geoScore > 0 && aeoGrade !== "" && geoGrade !== "";
}

const AEO_SCORE_CATEGORIES = [
	"faq_readiness",
	"schema",
	"answerability",
	"technical",
	"readability",
	"content_depth",
	"internal_linking",
	"multimodal",
	"crawlability",
	"metadata",
];

const GEO_SCORE_CATEGORIES = [
	"brand_entity",
	"trust_signals",
	"ai_citation",
	"freshness",
	"sentiment",
	"brand_recognition",
];

export function filterCategoryScoresByAeo(categoryScores) {
	if (!Array.isArray(categoryScores)) return [];
	return categoryScores.filter((cs) => AEO_SCORE_CATEGORIES.includes(cs.category));
}

export function filterCategoryScoresByGeo(categoryScores) {
	if (!Array.isArray(categoryScores)) return [];
	return categoryScores.filter((cs) => GEO_SCORE_CATEGORIES.includes(cs.category));
}

/** Allow in-app return links under AI Visibility only. */
export function safeReturnTo(raw) {
	const value = String(raw || "").trim();
	if (!value.startsWith("/aivisibility/")) return null;
	return value;
}

export function monitoringAnalysisStrategyReturnPath(analysisId) {
	if (analysisId == null || analysisId === "") return null;
	const params = new URLSearchParams();
	params.set("analysisId", String(analysisId));
	return `/aivisibility/monitoring/analysis?${params.toString()}`;
}
